/**
 * 格式化工具函数
 * 统一管理所有格式化相关的工具函数
 */

import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/zh-cn'

dayjs.extend(relativeTime)
dayjs.locale('zh-cn')

/**
 * 格式化日期时间
 * @param date 日期字符串或Date对象
 * @param format 格式字符串，默认为 'YYYY-MM-DD HH:mm:ss'
 * @returns 格式化后的日期字符串
 */
export function formatDateTime(date: string | Date | null | undefined, format = 'YYYY-MM-DD HH:mm:ss'): string {
  if (!date) return ''
  
  const d = dayjs(date)
  if (!d.isValid()) return ''
  
  return d.format(format)
}

/**
 * 格式化日期
 * @param date 日期字符串或Date对象
 * @param format 格式字符串，默认为 'YYYY-MM-DD'
 * @returns 格式化后的日期字符串
 */
export function formatDate(date: string | Date | null | undefined, format = 'YYYY-MM-DD'): string {
  return formatDateTime(date, format)
}

/**
 * 格式化时间
 * @param date 日期字符串或Date对象
 * @param format 格式字符串，默认为 'HH:mm:ss'
 * @returns 格式化后的时间字符串
 */
export function formatTime(date: string | Date | null | undefined, format = 'HH:mm:ss'): string {
  return formatDateTime(date, format)
}

/**
 * 获取相对时间描述
 * @param date 日期字符串或Date对象
 * @returns 相对时间描述，如 "刚刚"、"5分钟前"、"2小时前" 等
 */
export function getRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return ''
  
  const d = dayjs(date)
  if (!d.isValid()) return ''
  
  return d.fromNow()
}

/**
 * 判断是否为今天
 * @param date 日期字符串或Date对象
 * @returns 是否为今天
 */
export function isToday(date: string | Date | null | undefined): boolean {
  if (!date) return false
  
  const d = dayjs(date)
  if (!d.isValid()) return false
  
  return d.isSame(dayjs(), 'day')
}

/**
 * 判断是否为昨天
 * @param date 日期字符串或Date对象
 * @returns 是否为昨天
 */
export function isYesterday(date: string | Date | null | undefined): boolean {
  if (!date) return false
  
  const d = dayjs(date)
  if (!d.isValid()) return false
  
  return d.isSame(dayjs().subtract(1, 'day'), 'day')
}

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @param decimals 小数位数，默认为 2
 * @returns 格式化后的文件大小字符串，如 "1.5 MB"
 */
export function formatFileSize(bytes: number | null | undefined, decimals = 2): string {
  if (!bytes || bytes === 0) return '0 Bytes'
  
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB']
  
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

/**
 * 格式化时长（秒数转换为时分秒格式）
 * @param seconds 秒数
 * @param showMilliseconds 是否显示毫秒，默认为 false
 * @returns 格式化后的时长字符串，如 "1:23:45" 或 "23:45" 或 "1:23:45.123"
 */
export function formatDuration(seconds: number | null | undefined, showMilliseconds = false): string {
  if (!seconds || seconds < 0) return '00:00'
  
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  const milliseconds = showMilliseconds ? Math.floor((seconds % 1) * 1000) : 0
  
  if (hours > 0) {
    if (showMilliseconds) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`
    }
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  } else {
    if (showMilliseconds) {
      return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
}

/**
 * 格式化数字（支持千分位）
 * @param num 数字
 * @param decimals 小数位数，默认为 0
 * @param locale 语言环境，默认为 'zh-CN'
 * @returns 格式化后的数字字符串，如 "1,234,567"
 */
export function formatNumber(num: number | null | undefined, decimals = 0, locale = 'zh-CN'): string {
  if (num === null || num === undefined) return '0'
  
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num)
}

/**
 * 格式化货币
 * @param amount 金额
 * @param currency 货币符号，默认为 '¥'
 * @param decimals 小数位数，默认为 2
 * @returns 格式化后的货币字符串
 */
export function formatCurrency(amount: number | null | undefined, currency = '¥', decimals = 2): string {
  if (amount === null || amount === undefined) return `${currency}0.00`
  
  return `${currency}${amount.toFixed(decimals)}`
}

/**
 * 格式化百分比
 * @param value 数值（0-1之间的小数或0-100之间的整数）
 * @param decimals 小数位数，默认为 1
 * @param isDecimal 是否为小数形式（0-1），默认为 false（0-100）
 * @returns 格式化后的百分比字符串
 */
export function formatPercent(value: number | null | undefined, decimals = 1, isDecimal = false): string {
  if (value === null || value === undefined) return '0%'
  
  const percentValue = isDecimal ? value * 100 : value
  return `${percentValue.toFixed(decimals)}%`
}

/**
 * 格式化大数字（添加千分位）
 * @param num 数字
 * @returns 格式化后的数字字符串，如 "1,234,567"
 */
export function formatLargeNumber(num: number | null | undefined): string {
  if (num === null || num === undefined) return '0'
  
  return num.toLocaleString('zh-CN')
}

/**
 * 格式化字节数（与formatFileSize相同，但返回格式不同）
 * @param bytes 字节数
 * @param decimals 小数位数，默认为 2
 * @returns 格式化后的字节数字符串
 */
export function formatBytes(bytes: number | null | undefined, decimals = 2): string {
  return formatFileSize(bytes, decimals)
}

